import { useEffect, useState } from "preact/hooks";
import { ACCESS_LABELS, FAC_LABELS, LAYERS, PERIOD_COLORS, dishLabel, kindLabel, periodLabel } from "../data/layers";
import type { EquinePoint, Feature, FoodPoint, GaaPoint, LayerPoint, NaturePoint, SessionPoint, Site, SortKey } from "../types";
import { filteredAll, focusOn, followMap, listItems, listScrollRequest, mapBounds, nextAtPlace, radiusCenter, saved, searchTokens, select, sort, toggleSaved, type Scored } from "../state/store";
import { fmtShort } from "./Events";
import { commonsThumb, fmtDateRange, fmtKm } from "../utils/format";
import { bboxOf } from "../utils/geo";
import { focusBBox } from "../state/store";

const PAGE = 60;

function Tag({ label, color }: { label: string; color?: string }) {
  return <span class="tag" style={color ? { "--tag": color } : undefined}>{label}</span>;
}

function CardMeta({ f }: { f: Feature }) {
  switch (f.layer) {
    case "history": {
      const s = f.raw as Site;
      return (
        <div class="meta">
          <b>{fmtDateRange(s.date_start, s.date_end) || "—"}</b>
          <span>{ACCESS_LABELS[s.accessibility ?? ""] ?? "—"}</span>
          <span>{FAC_LABELS[s.facilities_level ?? ""] ?? "—"}</span>
        </div>
      );
    }
    case "food": {
      const p = f.raw as FoodPoint;
      return <div class="meta">{p.price && <b>{p.price}</b>}<span>{(p.kinds || []).map(kindLabel).join(", ")}</span>{p.since && <span>since {p.since}</span>}</div>;
    }
    case "gaa": {
      const p = f.raw as GaaPoint;
      const next = nextAtPlace.value.get(`gaa:${p.id}`);
      return <div class="meta"><span>{(p.kinds || []).map(kindLabel).join(", ")}</span>{p.home && <b>{p.home}</b>}{next ? <span>next {fmtShort(next)}</span> : p.capacity ? <span>{p.capacity.toLocaleString()} capacity</span> : null}</div>;
    }
    case "equine": {
      const p = f.raw as EquinePoint;
      const next = nextAtPlace.value.get(`equine:${p.id}`);
      return <div class="meta"><span>{(p.kinds || []).map(kindLabel).join(", ")}</span>{next ? <b>next {fmtShort(next)}</b> : p.surface ? <span>{p.surface}</span> : null}</div>;
    }
    case "nature": {
      const p = f.raw as NaturePoint;
      return <div class="meta"><span>{(p.kinds || []).map(kindLabel).join(", ")}</span>{p.geology?.rock && <b>{p.geology.rock}</b>}</div>;
    }
    case "sessions": {
      const s = f.raw as SessionPoint;
      return <div class="meta"><b>{s.town}</b><span>Trad session, check night on The Session</span></div>;
    }
  }
}

function CardTags({ f }: { f: Feature }) {
  if (f.layer === "history") {
    const s = f.raw as Site;
    return (
      <div class="tags">
        {s.periods.slice(0, 3).map((p) => <Tag label={periodLabel(p)} color={PERIOD_COLORS[p]} />)}
        {s.tags?.includes("unesco") && <Tag label="UNESCO" color="#b83c2b" />}
        {s.accessibility === "remote" && <Tag label="Remote" color="#b83c2b" />}
      </div>
    );
  }
  if (f.layer === "food") {
    const p = f.raw as FoodPoint;
    return <div class="tags">{(p.dishes || []).slice(0, 4).map((d) => <Tag label={dishLabel(d)} color={LAYERS.food.color} />)}</div>;
  }
  return null;
}

export function Card({ item }: { item: Scored }) {
  const f = item.f;
  const def = LAYERS[f.layer];
  const starred = saved.value.has(f.key);
  const blurb = f.layer === "history" ? (f.raw as Site).description : f.layer === "sessions" ? undefined : (f.raw as LayerPoint & FoodPoint).locals_note || (f.raw as LayerPoint).summary;
  const photo = f.layer === "sessions" ? undefined : (f.raw as Site | LayerPoint).photo;
  return (
    <article class={`card layer-${f.layer} ${photo?.url ? "has-thumb" : ""}`} style={{ "--layer": def.color }}>
      <button type="button" class="btn-star" aria-pressed={starred} aria-label={starred ? "Remove star" : "Star this place"} onClick={() => toggleSaved(f.key)}>{starred ? "★" : "☆"}</button>
      {photo?.url && (
        <button type="button" class="thumb" onClick={() => select(f)} aria-label={`Open ${f.name}`}>
          <img src={commonsThumb(photo.url, 330)} alt="" loading="lazy" decoding="async" />
        </button>
      )}
      <h3><button type="button" class="card-title" onClick={() => select(f)}>{f.name}</button>{f.sub && <span class="name-sub">{f.sub}</span>}</h3>
      {item.dist != null && <div class="dist">{fmtKm(item.dist)} away</div>}
      <CardMeta f={f} />
      <CardTags f={f} />
      {blurb && <p class="blurb">{blurb}</p>}
      {searchTokens.value.length > 0 && item.primary === "description" && <span class="match-badge">matched description</span>}
      <div class="actions">
        <button type="button" class="btn-map" onClick={() => { select(f); focusOn(f); }}>Show on map</button>
        <button type="button" class="btn-details" onClick={() => select(f)}>Details</button>
      </div>
    </article>
  );
}

export function ResultsList() {
  const items = listItems.value;
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [items.length, sort.value]);
  useEffect(() => {
    if (!listScrollRequest.value) return;
    document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [listScrollRequest.value]);

  const total = filteredAll.value.length;
  const inView = followMap.value && mapBounds.value && !searchTokens.value.length;
  const distEnabled = !!radiusCenter.value || items.some((x) => x.dist != null);
  const fitAll = () => {
    const b = bboxOf(filteredAll.value.map((x) => x.f));
    if (b) focusBBox(b);
  };

  return (
    <section class="results" id="results" aria-label="Results">
      <header class="results-head">
        <h2>
          {items.length} {items.length === 1 ? "place" : "places"}
          {inView && total !== items.length ? <span class="results-sub"> in view, <button type="button" class="btn-link" onClick={fitAll}>show all {total}</button></span> : null}
        </h2>
        <label class="sort">
          Sort
          <select value={sort.value} onChange={(e) => { sort.value = (e.target as HTMLSelectElement).value as SortKey; }}>
            <option value="relevance" disabled={!searchTokens.value.length}>Relevance</option>
            <option value="name">A → Z</option>
            <option value="date_asc">Oldest first</option>
            <option value="date_desc">Newest first</option>
            <option value="distance" disabled={!distEnabled}>Nearest</option>
          </select>
        </label>
      </header>
      {items.length === 0 ? (
        <p class="empty">Nothing here. {inView ? "Zoom out, or turn off “List follows the map view”." : "Try clearing some filters or switching on another layer."}</p>
      ) : (
        <div class="card-list">
          {items.slice(0, limit).map((it) => <Card key={it.f.key} item={it} />)}
        </div>
      )}
      {items.length > limit && (
        <button type="button" class="btn-ghost btn-more" onClick={() => setLimit(limit + PAGE)}>Show {Math.min(PAGE, items.length - limit)} more of {items.length}</button>
      )}
    </section>
  );
}
